import { Avatar, Stack, TextField, Typography } from '@mui/material';
import { getGeneralOptions } from 'API/generalOptions/generalOptions';
import { ControlledTextField } from 'Components/ControlledTextField';
import { OTPLoginContext } from 'Context/OTPLoginContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { UniversalRequestPurpleExclamationMark } from 'Images';
import { useContext, useEffect, useState } from 'react';

const phonePrefixLength = 3,
  phonePrefixStart = '0',
  totalPhoneNumberLength = 10,
  mobilePhoneFirstChar = '0',
  mobilePhoneSecondChar = '5';

export const ContactDetails = () => {
  const { userDetails, setUserDetails, errors, setErrors } =
    useContext(OTPLoginContext);
  const [phonePrefixes, setPhonePrefixes] = useState<{
    ids: string[];
    values: string[];
  }>({ ids: [''], values: [''] });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.OTP_Login
  ).personalDetailsForm;

  useEffect(() => {
    (async () => {
      const result = await getGeneralOptions();
      if (result) {
        const prefixesIds: string[] = [];
        const prefixesValues: string[] = [];
        result.general.phonePrefixes.map((prefix, index) => {
          if (
            prefix.value.length === phonePrefixLength &&
            prefix.value.charAt(0) === phonePrefixStart
          ) {
            prefixesIds.push(prefix.id.toString());
            prefixesValues.push(prefix.value);
          }
          return null;
        });
        setPhonePrefixes({ ids: prefixesIds, values: prefixesValues });
        setUserDetails({
          ...userDetails,
          phonePrefix: userDetails.phonePrefix || phonePrefixes.ids[0] || '050',
        });
      }
    })();
  }, []);

  const onBlurPhoneNumber = ({
    target: { value },
  }: React.FocusEvent<HTMLInputElement>) => {
    if (
      value.length < totalPhoneNumberLength ||
      value[0] !== mobilePhoneFirstChar ||
      value[1] !== mobilePhoneSecondChar
    ) {
      setPhoneError(dictionary.invalidPhone);
      return;
    } else setPhoneError('');
  };

  const onChangePhoneNumber = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(value.slice(0, totalPhoneNumberLength));
    let newUserDetails = { ...userDetails, phoneNumber: value };

    if (value.length === totalPhoneNumberLength) {
      const prefix = phonePrefixes.values.find(
        (val) => value.slice(0, phonePrefixLength) === val
      );
      if (prefix)
        newUserDetails = {
          ...newUserDetails,
          phoneNumber: newUserDetails.phoneNumber.slice(phonePrefixLength),
          phonePrefix: prefix,
        };
    }
    setUserDetails(newUserDetails);
  };

  useEffect(() => {
    if (userDetails.phoneNumber.length > 0) {
      setPhoneNumber(`${userDetails.phonePrefix}${userDetails.phoneNumber}`);
    }
  }, []);

  return (
    <Stack direction='column' gap='16px' mb='56px' component='fieldset'>
      <Typography
        variant={'body1'}
        component='legend'
        display={'block'}
        mb='16px'
      >
        {dictionary.contactDetails}
      </Typography>
      <TextField
        label={dictionary.phoneNumber}
        required
        type='tel'
        value={phoneNumber}
        onBlur={onBlurPhoneNumber}
        onChange={onChangePhoneNumber}
        size='small'
        sx={{
          bgcolor: 'white',
          borderRadius: '8px',
          width: '100%',

          '& fieldset': {
            border: '1px solid',
            borderColor: 'primary.shade_200',
            borderRadius: '8px',
          },
          '& label.Mui-focused': {
            color: '#1525E8',
            bgcolor: 'white',
          },
        }}
        error={phoneError !== ''}
        inputProps={{
          'aria-labelledby': phoneError !== '' ? 'phone-error' : undefined,
        }}
      />
      {phoneError !== '' && (
        <Typography
          m='-10px 0 0 7px'
          variant='body6'
          id='phone-error'
          color='#D12424'
          role='alert'
        >
          {phoneError}
        </Typography>
      )}

      <ControlledTextField
        label={dictionary.email}
        ariaLabel={dictionary.email}
        ariaDescribe='email-desc'
        value={userDetails.email}
        type='email'
        setValue={(value: string) =>
          setUserDetails({ ...userDetails, email: value })
        }
        showError
        inputRegexType='EMAIL'
        error={errors.email}
        setError={(error: string | null) =>
          setErrors({ ...errors, email: error })
        }
      />
      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='flex-start'
        gap='12px'
      >
        <Avatar
          sx={{ width: '20px', height: '20px', mt: '3px' }}
          src={UniversalRequestPurpleExclamationMark}
          alt={''}
        />
        <Typography
          id='email-desc'
          variant={'body3'}
          sx={{
            maxWidth: '47ch',
          }}
        >
          {dictionary.emailNotMandatory}
        </Typography>
      </Stack>
    </Stack>
  );
};
